import React from 'react';
import SelectDropdown from '../../components/select';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, Province } from './dialogTypes';

interface MechanicalContractorAddressInfoFormProps {
  formData: MechanicalContractorFormData;
  errors: MechanicalContractorFormErrors;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubOfficeChange: (index: number, field: string, value: string) => void;
  provinceOptions: Province[];
  isLoadingProvinces: boolean;
  setFormData: React.Dispatch<React.SetStateAction<MechanicalContractorFormData>>;
}

const MechanicalContractorAddressInfoForm: React.FC<MechanicalContractorAddressInfoFormProps> = ({
  formData,
  errors,
  handleInputChange,
  handleSubOfficeChange,
  provinceOptions,
  isLoadingProvinces,
  setFormData
}) => {
  const handleProvinceChange = (value: string) => {
    setFormData(prev => ({ ...prev, province: value }));
  };

  const provinceSelectOptions = provinceOptions.map(province => ({
    value: province.id,
    label: province.name
  }));

  return (
    <>
      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Address (Optional)
        </label>
        <input
          type="text"
          name="address"
          id="input-address"
          placeholder="Enter street address"
          className={`w-full px-3 py-2 text-body border ${errors.address ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.address}
          onChange={handleInputChange}
        />
        {errors.address && (
          <p className="mt-1 text-xs text-red-500">{errors.address}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          City (Optional)
        </label>
        <input
          type="text"
          name="city"
          id="input-city"
          placeholder="Enter city"
          className={`w-full px-3 py-2 text-body border ${errors.city ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.city}
          onChange={handleInputChange}
        />
        {errors.city && (
          <p className="mt-1 text-xs text-red-500">{errors.city}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Province (Optional)
        </label>
        <SelectDropdown
          options={provinceSelectOptions}
          selectedValue={formData.province}
          onSelect={handleProvinceChange}
          getValue={(option: any) => option.value}
          getDisplayText={(option: any) => option.label}
          placeholder="Select province"
          testId="select-province"
          disabled={isLoadingProvinces}
        />
        {errors.province && (
          <p className="mt-1 text-xs text-red-500">{errors.province}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Postal Code (Optional)
        </label>
        <input
          type="text"
          name="postalCode"
          id="input-postal-code"
          placeholder="Enter postal code"
          className={`w-full px-3 py-2 text-body border ${errors.postalCode ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.postalCode}
          onChange={handleInputChange}
        />
        {errors.postalCode && (
          <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>
        )}
      </div>

      {/* Sub Offices Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-small font-medium text-text-secondary">
            Sub Offices (Optional)
          </label>
        </div>
        <button
          id="btn-add-suboffice"
          type="button"
          onClick={() => {
            const updatedSubOffices = [...formData.subOffices];
            updatedSubOffices.push({
              address: '',
              city: '',
              province: '',
              postalCode: '',
              coordinates: undefined
            });
            setFormData({
              ...formData,
              subOffices: updatedSubOffices
            });
          }}
          className={"btn-secondary text-sm px-4 py-2"}
          title={'Add sub office'}
        >
          + Add Sub Office
        </button>
      </div>

      {formData.subOffices.map((office, index) => (
        <div key={index} className="mt-3 p-4 border border-gray-200 bg-gray-50 rounded-lg relative">
          <button
            id={`btn-remove-suboffice-${index}`}
            type="button"
            onClick={() => {
              const updatedSubOffices = [...formData.subOffices];
              updatedSubOffices.splice(index, 1);
              setFormData({
                ...formData,
                subOffices: updatedSubOffices
              });
            }}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            aria-label="Remove sub-office"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

          <h4 className="text-sm font-medium text-gray-800 mb-3">Sub Office {index + 1}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-small font-medium text-text-secondary mb-1">
                Address
              </label>
              <input
                type="text"
                id={`suboffice-address-${index}`}
                placeholder="Enter address"
                className="w-full px-3 py-2 text-body border border-border rounded-input focus:outline-none"
                value={office.address || ''}
                onChange={(e) => handleSubOfficeChange(index, 'address', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-small font-medium text-text-secondary mb-1">
                City
              </label>
              <input
                type="text"
                id={`suboffice-city-${index}`}
                placeholder="Enter city"
                className="w-full px-3 py-2 text-body border border-border rounded-input focus:outline-none"
                value={office.city || ''}
                onChange={(e) => handleSubOfficeChange(index, 'city', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-small font-medium text-text-secondary mb-1">
                Province
              </label>
              {isLoadingProvinces ? (
                <div className="w-full px-3 py-2 text-body border border-border rounded-input text-gray-500">
                  Loading province options...
                </div>
              ) : (
                <SelectDropdown
                  testId={`sub-office-province-${index}`}
                  options={provinceSelectOptions}
                  getValue={(option: any) => option.value}
                  getDisplayText={(option: any) => option.label}
                  onSelect={(value) => handleSubOfficeChange(index, 'province', value)}
                  selectedValue={office.province}
                  placeholder="Select province"
                />
              )}
            </div>

            <div>
              <label className="block text-small font-medium text-text-secondary mb-1">
                Postal Code
              </label>
              <input
                type="text"
                id={`suboffice-postalCode-${index}`}
                placeholder="Enter postal code"
                className={`w-full px-3 py-2 text-body border ${errors.subOffices?.[index]?.postalCode ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none`}
                value={office.postalCode || ''}
                onChange={(e) => handleSubOfficeChange(index, 'postalCode', e.target.value)}
              />
              {errors.subOffices?.[index]?.postalCode && (
                <p className="mt-1 text-xs text-red-500">{errors.subOffices[index].postalCode}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default MechanicalContractorAddressInfoForm;